/**
 * FLIP 与飞行关键帧的**纯函数**层（§11.6 60fps 保障第 1、2 条）。
 *
 * 这里一个 DOM 读取都没有：起止点全部来自 `useTableGeometry` 缓存的 `TableLayout`
 * （红线 #47）。FLIP 的 "First" 不靠 `getBoundingClientRect`，而是靠上一帧的
 * 布局对象——布局本来就是纯函数算出来的，前后两份一比就是位移。
 *
 * 另一条硬约束（红线 #46）：所有关键帧只出现 `transform` 与 `opacity`，
 * 绝不出现 width/height/top/left/box-shadow/filter。
 */

import type { SeatIndex } from '@poker/shared'

import type { Point, TableLayout } from '../table/seatGeometry.js'

/** 关键帧字符串里保留两位小数：既够平滑，又让单测能做精确断言 */
const r2 = (n: number): number => Math.round(n * 100) / 100

function translate(dx: number, dy: number): string {
  return `translate3d(${r2(dx)}px, ${r2(dy)}px, 0)`
}

function transformOf(dx: number, dy: number, scale: number, rotate: number): string {
  const parts = [translate(dx, dy)]
  if (rotate !== 0) parts.push(`rotate(${r2(rotate)}deg)`)
  if (scale !== 1) parts.push(`scale(${r2(scale)})`)
  return parts.join(' ')
}

export interface Delta {
  readonly dx: number
  readonly dy: number
}

export function deltaBetween(from: Point, to: Point): Delta {
  return { dx: from.x - to.x, dy: from.y - to.y }
}

// ============================================================================
// 入场（元素已经在终点，只把它「倒推」回起点再放开）
// ============================================================================

export interface EnterOptions {
  /** 起始旋转角（发牌是 -14°） */
  readonly rotate?: number
  /** 起始缩放（发牌是 0.7） */
  readonly scale?: number
  readonly fadeIn?: boolean
}

/**
 * 发牌飞入 / 任意「从 A 点飞到自己位置」的入场帧。
 * 元素的 left/top 已经是终点（静态），所以终点帧恒为 identity——
 * 这样动画结束后即使 fill 为 none，元素也停在正确位置，不需要 forwards。
 */
export function enterFrames(from: Point, to: Point, o: EnterOptions = {}): Keyframe[] {
  const { dx, dy } = deltaBetween(from, to)
  const first: Keyframe = {
    transform: transformOf(dx, dy, o.scale ?? 1, o.rotate ?? 0),
    opacity: o.fadeIn === true ? 0 : 1,
  }
  return [first, { transform: 'translate3d(0px, 0px, 0)', opacity: 1 }]
}

// ============================================================================
// 飞行（精灵：自身没有 left/top，全靠 transform 定位）
// ============================================================================

export interface FlightOptions {
  readonly fromScale?: number
  readonly toScale?: number
  /** 末尾多少毫秒内淡出（收池是 80ms）。需要配合 duration 换算 offset。 */
  readonly fadeTailMs?: number
  readonly durationMs?: number
  /** 精灵自身尺寸，用于把「中心点」换算成 translate（精灵锚在左上角） */
  readonly size?: number
}

/**
 * 筹码精灵从 from 飞到 to。精灵是池化复用的裸 div，位置完全由 transform 决定，
 * 因此起止两端都要写绝对坐标（而不是像入场那样倒推）。
 */
export function flightFrames(from: Point, to: Point, o: FlightOptions = {}): Keyframe[] {
  const half = (o.size ?? 0) / 2
  const s0 = o.fromScale ?? 1
  const s1 = o.toScale ?? 1
  const start: Keyframe = {
    transform: transformOf(from.x - half, from.y - half, s0, 0),
    opacity: 1,
    offset: 0,
  }
  const end: Keyframe = {
    transform: transformOf(to.x - half, to.y - half, s1, 0),
    opacity: 1,
    offset: 1,
  }
  const tail = o.fadeTailMs ?? 0
  const dur = o.durationMs ?? 0
  if (tail > 0 && dur > tail) {
    // 末 tail 毫秒淡出：插一帧把 opacity 撑到淡出起点，避免整段线性变淡
    const offset = r2((dur - tail) / dur)
    const mid: Keyframe = {
      transform: transformOf(
        from.x + (to.x - from.x) * offset - half,
        from.y + (to.y - from.y) * offset - half,
        s0 + (s1 - s0) * offset,
        0,
      ),
      opacity: 1,
      offset,
    }
    return [start, mid, { ...end, opacity: 0 }]
  }
  return [start, end]
}

// ============================================================================
// 3D 翻牌
// ============================================================================

/**
 * 翻牌：`.card__inner` 从背面(0°)转到正面(180°)。
 * 正/反面各自 `backface-visibility: hidden`，正面预旋 180°，
 * 于是整段动画只有一个 transform 属性在动。
 */
export function flipFrames(reverse = false): Keyframe[] {
  const from = reverse ? 180 : 0
  const to = reverse ? 0 : 180
  return [
    { transform: `rotateY(${from}deg)` },
    { transform: `rotateY(${to}deg)` },
  ]
}

// ============================================================================
// 座位重排 FLIP
// ============================================================================

export interface SeatFlip {
  readonly seat: SeatIndex
  readonly dx: number
  readonly dy: number
}

/** 从一份布局里抽出「座位号 → 座位块中心」 */
export function seatCenters(layout: TableLayout): Map<SeatIndex, Point> {
  const m = new Map<SeatIndex, Point>()
  for (const s of layout.seats) m.set(s.seat, s.center)
  return m
}

/**
 * 两份布局之差 = 每个座位要「倒推」的位移。
 * 只返回真的动了的座位；阈值 0.5px 以下当作没动（浮点抖动不值得起一个合成层）。
 */
export function seatFlipDeltas(
  prev: ReadonlyMap<SeatIndex, Point>,
  next: ReadonlyMap<SeatIndex, Point>,
  epsilon = 0.5,
): SeatFlip[] {
  const out: SeatFlip[] = []
  for (const [seat, to] of next) {
    const from = prev.get(seat)
    if (from === undefined) continue
    const dx = from.x - to.x
    const dy = from.y - to.y
    if (Math.abs(dx) < epsilon && Math.abs(dy) < epsilon) continue
    out.push({ seat, dx, dy })
  }
  return out
}

/** FLIP 的 Invert→Play 两帧 */
export function flipTranslateFrames(dx: number, dy: number): Keyframe[] {
  return [{ transform: translate(dx, dy) }, { transform: 'translate3d(0px, 0px, 0)' }]
}

/** 赢家光晕：只动预置 ::after 式元素的 opacity / scale（红线 #46） */
export function glowFrames(scale = 1.08): Keyframe[] {
  return [
    { opacity: 0, transform: 'scale(1)' },
    { opacity: 1, transform: `scale(${r2(scale)})`, offset: 0.45 },
    { opacity: 0, transform: 'scale(1)' },
  ]
}

/** 行动气泡：scale(.8→1) + opacity + translateY */
export function bubbleFrames(totalMs: number, inMs: number, outMs: number): Keyframe[] {
  const t = Math.max(1, totalMs)
  const a = r2(Math.min(1, inMs / t))
  const b = r2(Math.max(a, 1 - outMs / t))
  return [
    { opacity: 0, transform: 'translate3d(0, 6px, 0) scale(0.8)', offset: 0 },
    { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)', offset: a },
    { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)', offset: b },
    { opacity: 0, transform: 'translate3d(0, -4px, 0) scale(0.96)', offset: 1 },
  ]
}
